import * as d3 from 'd3'
import {
  DEFAULT_FORCE,
  type ForceParams,
  type GraphLayout,
  type LayoutLink,
  type LayoutHandlers,
  type LayoutNode,
} from './types.ts'

// ============================================================================
// ForceLayout , the original d3-force simulation behind the GraphLayout
// interface. Continuous + interactive: ticks every frame and responds to live
// dragging by pinning a node's fx/fy.
// ============================================================================

export class ForceLayout implements GraphLayout {
  readonly kind = 'force' as const
  readonly interactive = true
  private sim: d3.Simulation<LayoutNode, undefined>
  private linkForce: d3.ForceLink<LayoutNode, LayoutLink>
  private nodes: LayoutNode[] = []
  /** Live force tuning (repulsion / link distance / collision). Seeded from the
   *  caller (so a layout swap inherits the current settings) and mutated by
   *  setForceParams. */
  private params: ForceParams

  constructor(
    private h: LayoutHandlers,
    params?: Partial<ForceParams>,
  ) {
    this.params = { ...DEFAULT_FORCE, ...params }
    this.linkForce = d3
      .forceLink<LayoutNode, LayoutLink>([])
      .id((d) => d.id)
      .distance(this.params.linkDistance)
      .strength(0.5)
    this.sim = d3
      .forceSimulation<LayoutNode>([])
      .force('link', this.linkForce)
      .force('charge', d3.forceManyBody<LayoutNode>().strength(this.params.charge).theta(0.9).distanceMax(400))
      .force('collide', d3.forceCollide<LayoutNode>(this.params.collideRadius))
      .force('x', d3.forceX<LayoutNode>(0).strength(0.03))
      .force('y', d3.forceY<LayoutNode>(0).strength(0.03))
      .alphaDecay(0.025)
      .on('tick', () => this.h.onTick())
      .on('end', () => this.h.onEnd())
    this.sim.stop()
  }

  getForceParams(): ForceParams {
    return { ...this.params }
  }

  /**
   * Retune the simulation's forces in place and re-energise it so the change is
   * visible. The headline use is dialling up repulsion (a more-negative `charge`)
   * to spread out a dense diagram.
   */
  setForceParams(p: Partial<ForceParams>) {
    this.params = { ...this.params, ...p }
    this.linkForce.distance(this.params.linkDistance)
    const charge = this.sim.force('charge') as d3.ForceManyBody<LayoutNode> | undefined
    charge?.strength(this.params.charge)
    // Collision may have been dropped for very large graphs (see setGraph); only
    // retune it if it's currently installed.
    const collide = this.sim.force('collide') as d3.ForceCollide<LayoutNode> | undefined
    collide?.radius(this.params.collideRadius)
    // Nudge alpha up (never down) so an idle layout actually re-settles around the
    // new forces without yanking a still-warm one.
    this.sim.alpha(Math.max(this.sim.alpha(), 0.3)).restart()
  }

  setGraph(nodes: LayoutNode[], links: LayoutLink[]) {
    this.nodes = nodes
    this.sim.nodes(nodes)
    this.linkForce.links(links)
    // Scale cost to size: big graphs settle fast and drop collision so the page
    // reaches idle quickly instead of churning.
    const n = nodes.length
    this.sim.alphaDecay(n > 4000 ? 0.08 : 0.0228)
    const hasCollide = !!this.sim.force('collide')
    if (n > 8000 && hasCollide) this.sim.force('collide', null)
    else if (n <= 8000 && !hasCollide) this.sim.force('collide', d3.forceCollide<LayoutNode>(this.params.collideRadius))
  }

  run(structural: boolean) {
    if (structural) this.sim.alpha(this.nodes.length > 4000 ? 0.12 : 0.6).restart()
  }

  reheat() {
    this.sim.alpha(0.9).restart()
  }

  wake() {
    if (this.nodes.length < 2000) this.sim.alpha(0.3).restart()
  }

  stop() {
    this.sim.stop()
  }

  dragStart(n: LayoutNode) {
    n.fx = n.x
    n.fy = n.y
    this.sim.alphaTarget(0.2).restart()
  }

  dragMove(n: LayoutNode, x: number, y: number) {
    n.fx = x
    n.fy = y
  }

  dragEnd(n: LayoutNode, pin: boolean) {
    if (!pin) {
      n.fx = null
      n.fy = null
    }
    this.sim.alphaTarget(0)
  }

  destroy() {
    this.sim.on('tick', null).on('end', null)
    this.sim.stop()
  }
}
